import React, { useState } from 'react'
import { useMutation } from '@apollo/client'
import { BusyIndicator } from '@/atoms/BusyIndicator'
import { Button } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { DELETE_PAYMENT_METHOD, UPDATE_DEFAULT_PAYMENT_METHOD, UPDATE_PAYMENT_METHOD } from '@/services/NftService'
import ccBrandOptions, { isValidCcBrand } from './ccBrandOptions'

type SavedPaymentMethodProps = {
  paymentMethodId: string
  paymentIntentId?: string
  brand: string
  last4: string
  expYear: string
  selectedCard?: string
  isSelectingPaymentMethod: boolean
  isDefaultPaymentMethod: boolean
  isLastCard: boolean
  savedPaymentMethodLoading: boolean
  handlePaymentMethodSelection: (paymentMethodId: string) => void
  closePaymentMethodSelection: () => void
  changeSavePaymentMethodLoading: (value: boolean) => void
  refetchCustomerPaymentMethods: () => void
}

const SavedPaymentMethod: React.FC<SavedPaymentMethodProps> = ({
  paymentMethodId,
  paymentIntentId,
  brand = 'default',
  last4,
  expYear,
  selectedCard,
  isSelectingPaymentMethod,
  isDefaultPaymentMethod,
  isLastCard,
  savedPaymentMethodLoading,
  handlePaymentMethodSelection,
  closePaymentMethodSelection,
  changeSavePaymentMethodLoading,
  refetchCustomerPaymentMethods,
}) => {
  const brandOptions = isValidCcBrand(brand) ? ccBrandOptions[brand] : ccBrandOptions.default

  const [isDeleted, setIsDeleted] = useState(false)

  const [updatePaymentMethod, { loading: updatePaymentMethodLoading }] = useMutation(UPDATE_PAYMENT_METHOD, {
    onCompleted: (data) => {
      handlePaymentMethodSelection(data?.updatePaymentMethod?.paymentMethodId)
      closePaymentMethodSelection()
      changeSavePaymentMethodLoading(false)
    },
    errorPolicy: 'all',
  })

  const [updateDefaultPaymentMethod] = useMutation(UPDATE_DEFAULT_PAYMENT_METHOD, {
    onCompleted: (data) => {
      refetchCustomerPaymentMethods()
      handlePaymentMethodSelection(data?.updateDefaultPaymentMethod?.defaultPaymentMethodId)
      closePaymentMethodSelection()
      changeSavePaymentMethodLoading(false)
    },
    errorPolicy: 'all',
  })

  const [deletePaymentMethod, { loading }] = useMutation(DELETE_PAYMENT_METHOD, {
    onCompleted: () => {
      refetchCustomerPaymentMethods()
      changeSavePaymentMethodLoading(false)
    },
    errorPolicy: 'all',
  })

  return (
    <>
      {(isSelectingPaymentMethod || selectedCard === paymentMethodId) && !isDeleted && (
        <div className="w-full">
          <div className="flex flex-col justify-center items-center">
            <div className="relative w-full flex flex-col justify-center items-center">
              <div className="flex flex-row p-4 justify-around items-center w-full mb-2 text-core-gray-800 flex-wrap bg-core-gray-700 rounded-xl">
                <div className="w-full mb-4">
                  <img src={brandOptions.imgSrc ?? ''} alt={brandOptions.imgAlt} />
                </div>
                <div className={`grid grid-cols-4 gap-x-2 text-xl text-white`}>
                  <span>****</span>
                  <span>{brand !== 'amex' && brand !== 'diners' ? '****' : '******'}</span>
                  {brand !== 'amex' && brand !== 'diners' && <span>****</span>}
                  <span className={`font-[PT Mono]`}>
                    {' '}
                    {brand !== 'amex' && brand !== 'diners' ? '' : '*'}
                    {last4}
                  </span>
                </div>
                <div className="text-white">
                  <span>**</span> / <span>{expYear}</span>
                </div>
              </div>

              {isSelectingPaymentMethod && isDefaultPaymentMethod && !isDeleted && (
                <div className="absolute -top-3 w-full">
                  <div
                    className={`w-2/3 mx-auto mb-4 p-2 text-center ${brandOptions.textColor} ${brandOptions.bgColor} rounded-xl`}
                  >
                    Default Payment Method
                  </div>
                </div>
              )}

              {isSelectingPaymentMethod && !isDeleted && (
                <div>
                  <Button
                    className="absolute -top-5 -right-5 rounded-full p-2"
                    variant="copper"
                    title="Delete Payment Method"
                    disabled={savedPaymentMethodLoading}
                    onClick={() => {
                      changeSavePaymentMethodLoading(true)
                      deletePaymentMethod({
                        variables: {
                          input: {
                            paymentMethodId,
                          },
                        },
                      })
                      setIsDeleted(true)
                    }}
                  >
                    {loading ? (
                      <BusyIndicator width={16} showBusy={savedPaymentMethodLoading} />
                    ) : (
                      <CloseIcon className="w-6" color="white" />
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {isSelectingPaymentMethod && !isDeleted && (
        <div>
          <div className="mx-auto max-w-[400px] flex flex-row justify-around items-center gap-4 mt-6">
            <div>
              <Button
                className="text-xs"
                variant="oxide"
                disabled={savedPaymentMethodLoading}
                onClick={() => {
                  changeSavePaymentMethodLoading(true)
                  updatePaymentMethod({
                    variables: {
                      input: {
                        paymentMethodId,
                        paymentIntentId,
                      },
                    },
                  })
                }}
              >
                {savedPaymentMethodLoading ? (
                  <BusyIndicator width={16} showBusy={savedPaymentMethodLoading} />
                ) : (
                  `Use ${last4}`
                )}
              </Button>
            </div>
            {!isDefaultPaymentMethod && !isDeleted && (
              <div>
                <Button
                  className="text-xs"
                  variant="lightgray"
                  disabled={updatePaymentMethodLoading}
                  onClick={() => {
                    changeSavePaymentMethodLoading(true)
                    updateDefaultPaymentMethod({
                      variables: {
                        input: { paymentMethodId, paymentIntentId },
                      },
                    })
                  }}
                >
                  {savedPaymentMethodLoading ? (
                    <BusyIndicator width={16} showBusy={savedPaymentMethodLoading} />
                  ) : (
                    'Set as default'
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      {isSelectingPaymentMethod && !isLastCard && !isDeleted && (
        <div>
          <div className="mx-auto w-3/4 border-b-2 border-core-gray-400 my-8" />
        </div>
      )}
    </>
  )
}

export default SavedPaymentMethod
